import React, { useState, useEffect } from 'react'
import {
  Flex,
  Avatar,
  IconButton,
  Confirmation,
  Toaster,
  EmployeeSelect,
  Button,
  IGridTableColumn,
  NewGridTable,
  GridTableStatusEnum,
} from '@ui'
import { Classes, H5, InputGroup, Spinner } from '@blueprintjs/core'
import { ApolloError } from 'apollo-boost'
import useAssetEmployees from './hooks/useAssetEmployees'
import { useSelection } from '@hooks'
import api, { IEmployee } from '@api'
import { pluralize } from '@utils'
import track from '@track'
import Event from '@track/Event'
import { Permissions, PermissionsType, IPermissionsOnAsset } from '@permissions'
import classnames from 'classnames'
import TableWrapper from './TableWrapper'

export default function AssetEmployees({
  id,
  reloadAssets,
  reloadAsset,
}: {
  id: string
  reloadAssets?: () => void
  reloadAsset?: () => void
}) {
  const selection = useSelection()
  const employees = useAssetEmployees(id)
  const [showSearch, setShowSearch] = useState(false)
  const [withUndo, setWithUndo] = useState<IEmployee[]>([])
  const [disableConfirmation, setDisableConfirmation] = useState(false)

  useEffect(() => {
    setWithUndo(employees.list)
  }, [employees.list])

  const helperText = selection.count
    ? `${selection.count} selected`
    : employees.query
    ? `${employees.count} filtered ${pluralize(employees.count, 'record')}`
    : `${employees.count} ${pluralize(employees.count, 'record')}`

  const reload = async () => {
    await reloadAssets?.()
    await reloadAsset?.()
    await employees.reload()
  }

  const columnsEmployees: IGridTableColumn<IEmployee>[] = [
    {
      label: 'Employee',
      Cell: ({ skeleton, cell }) => {
        return cell ? (
          <Flex align="center">
            <Avatar name={cell.data.name} assets={cell.data.assets} size={Avatar.Size.XS} />
            <div style={{ marginLeft: 10 }}>
              {cell.data.name}
              <div style={{ fontSize: 12, color: 'var(--grey1)' }}>{cell.data.email}</div>
            </div>
          </Flex>
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            placeholder
          </div>
        )
      },
    },
    {
      label: 'Department / Team',
      Cell: ({ cell, skeleton }) => {
        return cell ? (
          <div>
            {`${cell.data.dept ? cell.data.dept.name : ''} ${
              cell.data.team ? ' | ' + cell.data.team.name : ''
            }`}
          </div>
        ) : (
          <div
            className={classnames({
              [Classes.SKELETON]: skeleton,
            })}
          >
            place
          </div>
        )
      },
    },
    {
      label: '',
      Cell: ({ cell, isHighlighted }) => {
        if (!cell || !isHighlighted) return null

        const { data: employee } = cell

        return (
          <Permissions type={PermissionsType.ASSET}>
            {(permissions) => {
              const { canRemoveEmployees } = permissions as IPermissionsOnAsset
              return canRemoveEmployees ? (
                <div className="w-full" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <IconButton
                    round
                    minimal
                    tooltip={`Remove ${employee.name}`}
                    className="remove-icon"
                    iconName="times"
                    onClick={() => {
                      const removedEmployee = employee

                      setWithUndo((prev) => [...prev.filter((i) => employee.id !== i.id)])
                      selection.clear()
                      track.assetDialog({
                        eventName: Event.RECORD_REMOVE,
                        props: {
                          component: 'Employee Manage',
                        },
                      })
                      api.asset
                        .update(id, undefined, { disconnect: [{ id: employee.id }] }, undefined)
                        .then(() => {
                          reload()
                          Toaster.success({
                            action: {
                              onClick: () => {
                                api.asset
                                  .update(id, undefined, {
                                    connect: [{ id: employee.id }],
                                  })
                                  .then(reload)
                                  .catch((error: ApolloError) => Toaster.fail({ error }))
                                setWithUndo((prev) => [removedEmployee, ...prev])

                                track.assetDialog({
                                  eventName: Event.UNDO_CLICK,
                                  props: {
                                    component: 'Employee Manage',
                                    actionName: 'remove',
                                  },
                                })
                              },
                              text: 'Undo',
                            },
                            message: 'Asset removed from Employee',
                          })
                        })
                        .catch((error: ApolloError) => Toaster.fail({ error }))
                    }}
                  />
                </div>
              ) : null
            }}
          </Permissions>
        )
      },
    },
  ]

  const formatItems = (items: IEmployee[]) => {
    return items.map((i) => ({
      data: i,
    }))
  }

  const formatedItems = formatItems(employees.list)

  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        style={{
          marginTop: 40,
          height: 40,
        }}
      >
        <Flex align="center">
          <H5 style={{ marginBottom: 0, marginRight: 6, lineHeight: '30px' }}>Employees</H5>
          {showSearch ? (
            <InputGroup
              autoFocus
              leftIcon="search"
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                employees.run(e.target.value)
              }}
              placeholder="Search employees..."
              rightElement={
                <RightElement
                  showWithoutQuery
                  query={employees.query}
                  onClear={() => {
                    if (employees.query) employees.run('')
                    setShowSearch(false)
                  }}
                  isQueryResultLoading={employees.isLoading}
                />
              }
              value={employees.query}
            />
          ) : (
            <IconButton round minimal iconName="search" onClick={() => setShowSearch(true)} />
          )}
        </Flex>
      </Flex>
      <Flex justify="space-between">
        <Flex align="center">
          {Boolean(selection.count) && (
            <span style={{ paddingRight: 10 }}>
              <Confirmation
                shouldHide={disableConfirmation}
                enabled={!disableConfirmation}
                text={`Remove Asset from ${pluralize(selection.count, 'employee')}?`}
                onConfirm={() => {
                  const employeesForDelete = withUndo.filter((i) =>
                    Object.keys(selection.items).includes(i.id)
                  )

                  const employeeIds = Object.entries(selection.items)
                    .filter(([_, value]) => Boolean(value))
                    .map(([id]) => id)

                  setWithUndo((prev) => [...prev.filter((i) => !employeeIds.includes(i.id))])

                  api.asset
                    .update(id, undefined, {
                      disconnect: employeeIds.map((id) => ({ id: id })),
                    })
                    .then(() =>
                      setWithUndo((prev) => [...prev.filter((i) => !employeeIds.includes(i.id))])
                    )
                    .catch((error: ApolloError) => Toaster.fail({ error }))
                    .then(() => {
                      reload()

                      track.assetDialog({
                        eventName: Event.BATCH_ACTION,
                        props: {
                          actionName: 'Delete employees',
                          actionValue: null,
                          batchCount: employeeIds.length,
                        },
                      })

                      selection.clear()
                      Toaster.success({
                        action: {
                          onClick: () => {
                            setWithUndo((prev) => [...employeesForDelete, ...prev])
                            setDisableConfirmation(false)
                            api.asset
                              .update(id, undefined, {
                                connect: employeeIds.map((id) => ({ id: id })),
                              })
                              .then(reload)
                              .catch((error: ApolloError) => Toaster.fail({ error }))
                            track.assetDialog({
                              eventName: Event.UNDO_CLICK,
                              props: {
                                component: 'Employee Manage',
                                actionName: 'remove',
                              },
                            })
                          },
                          text: 'Undo',
                        },
                        message: `Asset removed from ${pluralize(selection.count, 'Employee')}`,
                      })
                    })
                }}
              >
                <Button text="Remove" />
              </Confirmation>
            </span>
          )}
          <Flex align="baseline">
            {!employees.isLoading && <span style={{ color: 'var(--grey1)' }}>{helperText}</span>}
            {employees.isLoading && placeholder}
            {Boolean(selection.count) && (
              <IconButton
                minimal
                tooltip="Clear selection"
                iconName="times"
                style={{ marginLeft: 4 }}
                data-button="clear_selection"
                onClick={() => {
                  selection.clear()
                  track.assetDialog({
                    eventName: Event.CLEAR_SELECTION,
                    props: {
                      component: 'Employee Manage',
                    },
                  })
                }}
              />
            )}
          </Flex>
        </Flex>
        <Permissions type={PermissionsType.ASSET}>
          {(permissions) => {
            const { canAddEmployees } = permissions as IPermissionsOnAsset
            return canAddEmployees ? (
              <EmployeeList
                alreadyAssigned={employees.list}
                onChangeEmployee={(empl) => {
                  api.asset
                    .update(id, undefined, { connect: [{ id: empl.id }] })
                    .then(() =>
                      Toaster.success({
                        message: 'Asset added to Employee',
                      })
                    )
                    .catch((error: ApolloError) => Toaster.fail({ error }))
                    .then(reload)

                  track.assetDialog({
                    eventName: Event.RECORD_ADD,
                    props: {
                      component: 'Employee Manage',
                    },
                  })
                }}
              />
            ) : (
              <div />
            )
          }}
        </Permissions>
      </Flex>
      <Permissions type={PermissionsType.ASSET}>
        {(permissions) => {
          const { canRemoveEmployees } = permissions as IPermissionsOnAsset
          return (
            <TableWrapper withSelection={canRemoveEmployees}>
              {/* // if no ability to remove employees, we're removing selection //
            and making the 1st column wider */}
              <NewGridTable<IEmployee>
                status={
                  employees.isLoading ? GridTableStatusEnum.LOADING : GridTableStatusEnum.IDLE
                }
                sticky={false}
                noDataMessage={employees.query ? undefined : 'No Employees associated yet'}
                columns={columnsEmployees}
                selection={canRemoveEmployees ? selection : undefined}
                data={formatedItems}
                skeletonSize={3}
              />
            </TableWrapper>
          )
        }}
      </Permissions>

      {employees.list && employees.pageInfo.hasNextPage && (
        <Button
          minimal
          style={{ marginTop: 5 }}
          text={`View ${
            employees.count - employees.list.length > 5
              ? '5'
              : employees.count - employees.list.length
          } more`}
          onClick={() => {
            employees.loadMore()
            track.assetDialog({
              eventName: Event.PAGE,
              props: {
                component: 'Employee Manage',
              },
            })
          }}
        />
      )}
    </>
  )
}

function EmployeeList({
  onChangeEmployee,
  disabled,
}: {
  onChangeEmployee?: (empl: any) => void
  disabled?: boolean
  alreadyAssigned?: IEmployee[]
}) {
  return (
    <EmployeeSelect
      value={null}
      onChange={(employee) => onChangeEmployee?.(employee)}
      placeholder="Employee"
      buttonProps={{
        disabled,
        iconName: 'plus',
        'data-button': 'add_employee',
      }}
    />
  )
}

function RightElement({
  showWithoutQuery,
  query,
  onClear,
  isQueryResultLoading,
}: {
  query: string
  onClear: () => void
  isQueryResultLoading: boolean
  showWithoutQuery?: boolean
}) {
  if (isQueryResultLoading && query) return <Spinner size={18} />
  if ((showWithoutQuery !== undefined && showWithoutQuery) || query)
    return (
      <IconButton
        minimal
        iconName="times"
        onClick={() => {
          onClear()
          track.assetDialog({
            eventName: Event.CLEAR_FILTER,
            props: {
              component: 'Employee Manage',
              filterName: 'Query',
              filterValue: null,
            },
          })
        }}
      />
    )
  return null
}

const placeholder = <span className={Classes.SKELETON}>placeholder</span>
